import { useLayoutEffect, useRef } from "react";

/**
 * Escape condiviso tra pannelli, drawer e modali.
 *
 * Un solo listener globale in fase capture: alla pressione di Escape si chiude
 * SOLO il pannello piu' in alto (layer maggiore, a parita' il piu' recente) e
 * l'evento viene fermato, cosi' i pannelli sottostanti restano aperti.
 *
 * L'evento viene invece lasciato passare (nessuna chiusura, nessuno stop)
 * quando l'utente sta usando Escape "in locale": campo di testo con il focus,
 * tendina o menu aperto, elemento marcato `data-escape-local`. In quei casi
 * Escape serve a chiudere la tendina o annullare la modifica, non il pannello.
 */

type Entry = { layer: number; close: () => void };

/** Elementi dentro cui Escape ha un significato locale e non chiude il pannello. */
export const LOCAL_ESCAPE_SELECTOR =
  '[data-escape-local], [role="listbox"], [role="menu"], [aria-expanded="true"], .react-datepicker';

/** Input per cui Escape non ha senso "locale": sono pulsanti o spunte, non testo. */
const NON_TEXT_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "range",
  "color",
  "file",
  "image",
]);

/**
 * True se il bersaglio dell'evento sta usando Escape per conto suo:
 * campo di testo / textarea / select / contenteditable con il focus, oppure un
 * antenato che dichiara una tendina o un menu aperti (o `data-escape-local`).
 * Funzione pura: accetta qualunque oggetto con `tagName`, `closest`, ecc.
 */
export function escapeStaysLocal(target: EventTarget | null | undefined): boolean {
  if (!target || typeof target !== "object") return false;
  const el = target as Partial<HTMLElement> & { closest?: (s: string) => unknown };
  const tag = typeof el.tagName === "string" ? el.tagName.toUpperCase() : "";
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = String((el as Partial<HTMLInputElement>).type || "text").toLowerCase();
    if (!NON_TEXT_INPUT_TYPES.has(type)) return true;
  }
  if (el.isContentEditable === true) return true;
  if (typeof el.closest === "function" && el.closest(LOCAL_ESCAPE_SELECTOR)) return true;
  return false;
}

/**
 * Sceglie il pannello da chiudere: layer piu' alto, a parita' l'id piu' alto
 * (cioe' l'ultimo aperto). Funzione pura, testabile senza DOM.
 */
export function pickTopEntry<T extends Entry>(entries: Iterable<[number, T]>): T | null {
  let best: [number, T] | null = null;
  for (const entry of entries) {
    if (
      !best ||
      entry[1].layer > best[1].layer ||
      (entry[1].layer === best[1].layer && entry[0] > best[0])
    )
      best = entry;
  }
  return best ? best[1] : null;
}

/**
 * Decide cosa fare con un evento Escape: `null` se va lasciato passare,
 * altrimenti l'entry da chiudere. Separata dal listener per i test.
 */
export function resolveEscape<T extends Entry>(
  event: { key: string; defaultPrevented?: boolean; isComposing?: boolean; target: EventTarget | null },
  entries: Iterable<[number, T]>,
): T | null {
  if (event.key !== "Escape" || event.defaultPrevented || event.isComposing) return null;
  if (escapeStaysLocal(event.target)) return null;
  return pickTopEntry(entries);
}

let sequence = 0;
const active = new Map<number, Entry>();

function handleEscape(event: KeyboardEvent) {
  const top = resolveEscape(event, active.entries());
  if (!top) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  top.close();
}

export function useDrawerEscape(
  open: boolean,
  close: () => void,
  layer: number,
) {
  const latest = useRef(close);
  latest.current = close;
  useLayoutEffect(() => {
    if (!open) return;
    const id = ++sequence;
    if (!active.size) document.addEventListener("keydown", handleEscape, true);
    active.set(id, { layer, close: () => latest.current() });
    return () => {
      active.delete(id);
      if (!active.size)
        document.removeEventListener("keydown", handleEscape, true);
    };
  }, [open, layer]);
}
